import { type RenderTask } from 'pdfjs-dist';
import { memo, type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { Spinner } from '@air/primitive-spinner';
import { cn } from '@air/utils-tailwind';

import { type PDFPagesCache } from '../../../../utils/PDFPagesCache';
import { type PDFViewerOutlineImagesCache } from '../../utils/PDFViewerOutlineImagesCache';
import { PDFViewerOutlinePageMenu } from './PDFViewerOutlinePageMenu';
import { getImageFromCanvas } from './utils/getImageFromCanvas';
import { getOutlineImageCanvasContext } from './utils/getOutlineImageCanvasContext';

export type RenderOutlinePageMenu = (params: {
  pageNumber: number;
  setMenuIsOpen: (isOpen: boolean) => void;
}) => JSX.Element;

type PDFViewerOutlinePageProps = {
  pageNumber: number;
  pdfPageCacheRef: RefObject<PDFPagesCache | undefined>;
  isSelected: boolean;
  onPageClick: (pageNumber: number) => void;
  thumbnailCacheRef: RefObject<PDFViewerOutlineImagesCache | undefined>;
  renderOutlinePageMenu?: RenderOutlinePageMenu;
};

export const PDF_OUTLINE_PAGE_THUMBNAIL_HEIGHT = 92;
const DRAW_UPSCALE_FACTOR = 1;

type ImageLoadingState = 'loading' | 'loaded' | 'error';

export const PDFViewerOutlinePage = memo(
  ({
    pageNumber,
    isSelected,
    onPageClick,
    pdfPageCacheRef,
    thumbnailCacheRef,
    renderOutlinePageMenu,
  }: PDFViewerOutlinePageProps) => {
    const imageContainerRef = useRef<HTMLDivElement>(null);
    const renderPromiseRef = useRef<RenderTask>();

    const [imageData, setImageData] = useState<string | undefined>();
    const [imageLoadingState, setImageLoadingState] = useState<ImageLoadingState>('loading');
    const isMounted = useRef<boolean>();

    useEffect(() => {
      isMounted.current = true;
      return () => {
        isMounted.current = false;
      };
    }, []);

    useEffect(() => {
      const loadPageImage = async () => {
        const pdfPage = await pdfPageCacheRef.current?.getPage(pageNumber);

        if (!pdfPage) {
          return;
        }

        const viewport = pdfPage.getViewport({ scale: 1 });
        const ratio = viewport.width / viewport.height;

        const imgHeight = PDF_OUTLINE_PAGE_THUMBNAIL_HEIGHT;
        const imgWidth = PDF_OUTLINE_PAGE_THUMBNAIL_HEIGHT * ratio;

        const scale = imgWidth / viewport.width;
        const drawViewport = pdfPage.getViewport({ scale: scale * DRAW_UPSCALE_FACTOR });

        // Render the thumbnail at a larger size and downsize the canvas (similar
        // to `setImage`), to improve consistency between thumbnails created by
        // the `draw` and `setImage` methods (fixes issue 8233).
        // NOTE: To primarily avoid increasing memory usage too much, but also to
        //   reduce downsizing overhead, we purposely limit the up-scaling factor.
        const { ctx, transform, canvas } = getOutlineImageCanvasContext({
          enableHMA: false,
          height: imgHeight,
          upscaleFactor: DRAW_UPSCALE_FACTOR,
          width: imgWidth,
        });

        if (!ctx) {
          return;
        }

        renderPromiseRef.current = pdfPage.render({
          canvasContext: ctx,
          transform,
          viewport: drawViewport,
        });

        renderPromiseRef.current.promise
          .then(() => {
            renderPromiseRef.current = undefined;

            if (!isMounted.current) {
              return;
            }

            const imageData = getImageFromCanvas({ canvas, height: imgHeight, width: imgWidth });

            if (imageData) {
              setImageLoadingState('loaded');
              setImageData(imageData);
              thumbnailCacheRef.current?.setThumbnail(pageNumber, imageData);
            } else {
              setImageLoadingState('error');
            }
          })
          .catch(() => {
            if (!isMounted.current) {
              return;
            }
            setImageLoadingState('error');
          })
          .finally(() => {
            // Zeroing the width and height causes Firefox to release graphics
            // resources immediately, which can greatly reduce memory consumption.
            canvas.width = 0;
            canvas.height = 0;
          });
      };

      const thumbnail = thumbnailCacheRef.current?.getThumbnail(pageNumber);

      if (thumbnail) {
        setImageData(thumbnail);
        setImageLoadingState('loaded');
      } else {
        loadPageImage().catch(() => {
          setImageLoadingState('error');
        });
      }

      return () => {
        renderPromiseRef.current?.cancel();
      };
    }, [pageNumber, pdfPageCacheRef, thumbnailCacheRef]);

    const handlePageClick = useCallback(() => {
      onPageClick(pageNumber);
    }, [onPageClick, pageNumber]);

    return (
      <div
        className="group/outline-page relative flex size-full"
        data-isselected={isSelected}
        data-page-number={pageNumber}
        data-testid="PDF_VIEWER_OUTLINE_PAGE"
      >
        <button
          className={cn(
            'flex h-full w-full flex-col items-center justify-center rounded-4 ads:bg-base bg-gray-1 px-0.5 pt-0.5',
            isSelected ? 'ads:bg-ads-gray-5 bg-gray-5' : 'ads:bg-base hover:ads:bg-ads-gray-6 hover:bg-gray-6',
          )}
          onClick={handlePageClick}
          type="button"
        >
          <div className="flex size-full items-center justify-center rounded-4 bg-black" ref={imageContainerRef}>
            {imageLoadingState === 'loading' && <Spinner className="size-4 ads:text-ads-cyan-10 text-teal-9" />}
            {imageLoadingState === 'loaded' && (
              <>
                {/* biome-ignore lint/performance/noImgElement: canvas thumbnail data URL; next/image does not apply */}
                <img
                  alt={`Thumbnail of page ${pageNumber}`}
                  className="pdfThumbnailImage bg-black object-contain"
                  src={imageData}
                  style={{ height: PDF_OUTLINE_PAGE_THUMBNAIL_HEIGHT }}
                />
              </>
            )}
          </div>
          <div className="py-2 font-bold ads:text-primary text-12 text-gray-12">Page {pageNumber}</div>
        </button>
        {!!renderOutlinePageMenu && (
          <PDFViewerOutlinePageMenu pageNumber={pageNumber} renderOutlinePageMenu={renderOutlinePageMenu} />
        )}
      </div>
    );
  },
);

PDFViewerOutlinePage.displayName = 'PDFViewerOutlinePage';
